import { defineStore } from 'pinia';
import useWishlistService from '~/composables/useWishlistService';
import { useCartStore } from '~/store/cart';

export type WishlistItem = {
  line: string;
  item: string;
  product: string;
  productName: string;
  variantName?: string;
  size?: string;
  ean?: string;
  sku?: string;
  quantity: number;
  unitPrice: string;
  totalPrice: string;
  productUrl?: string;
  imageUrl?: string;
};

export const useWishlistStore = defineStore('wishlist', {
  state: () => ({
    items: [] as WishlistItem[],
    isLoading: false,
    error: null as any,
  }),
  actions: {
    async loadWishlist() {
      this.isLoading = true;
      this.error = null;
      const svc = useWishlistService();
      const res = await svc.fetchWishlist();
      
      if (res) {
        // Handle different response formats from Centra
        if (res.items) {
          this.items = res.items;
        } else if (Array.isArray(res)) {
          this.items = res;
        } else if (res.wishlist && res.wishlist.items) {
          this.items = res.wishlist.items;
        } else {
          // Empty wishlist - not an error
          this.items = [];
        }
      } else {
        this.error = svc.lastError;
      }
      this.isLoading = false;
    },
    async addItem(item: string) {
      this.error = null;
      const svc = useWishlistService();
      
      // Check if this is the first item
      const wasEmpty = this.items.length === 0;
      
      // Optimistically add to UI (use displayItem for comparison)
      const displayItem = item.split('-')[0];
      const optimisticItem: WishlistItem = {
        line: 'temp-' + Date.now(),
        item: displayItem,
        product: displayItem,
        productName: '',
        quantity: 1,
        unitPrice: '',
        totalPrice: '',
      };
      this.items.unshift(optimisticItem);
      
      const res = await svc.addItemToWishlist(item);
      if (res) {
        // Reload to get actual data
        await this.loadWishlist();
        
        // If this was the first item, trigger wishlist highlight animation
        if (wasEmpty && this.items.length > 0) {
          const cartStore = useCartStore();
          cartStore.highlightWishlist = true;
          cartStore.showAccountMenu = true;
          
          // Turn off highlight after animation (animation is 1s x 2 iterations = 2s, turn off after 2s)
          setTimeout(() => {
            cartStore.highlightWishlist = false;
          }, 2000);
        }
        
        return true;
      } else {
        // Remove optimistic item on failure
        this.items = this.items.filter(i => i.line !== optimisticItem.line);
        this.error = svc.lastError;
        return false;
      }
    },
    async removeItem(lineId: string) {
      this.error = null;
      const svc = useWishlistService();
      
      // Optimistically remove from UI
      const removedItem = this.items.find(item => item.line === lineId);
      if (removedItem) {
        this.items = this.items.filter(item => item.line !== lineId);
      }
      
      const res = await svc.removeItemFromWishlist(lineId);
      if (res) {
        // Success - item already removed from UI
        return true;
      } else {
        // On error, restore the item
        if (removedItem) {
          this.items.push(removedItem);
        }
        this.error = svc.lastError;
        return false;
      }
    },
    isInWishlist(itemId: string): boolean {
      // Extract displayItem from itemId (format: "2385-4778" -> "2385")
      const displayItem = itemId.split('-')[0];
      // Compare with stored displayItem (which is now in the item field)
      return this.items.some(item => item.item === displayItem);
    },
  },
  getters: {
    itemCount: (state) => state.items.length,
  },
});
